import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import styles from "./LeadForm.module.css";

function LeadForm() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    toggle: "deal",
    description: "",
    client: "",
    place: "",
    eventName: "",
    dateBegin: "",
    dateEnd: "",
  });

  const [dateError, setDateError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // success modal
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("Заявка успешно создана");

  // inline error alert
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const clearError = () => {
    setErrorTitle("");
    setErrorMessage("");
  };

  // auth guard
  useEffect(() => {
    if (!currentUser) navigate("/");
  }, [currentUser, navigate]);

  // default dates (today)
  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const minDate = `${yyyy}-${mm}-${dd}`;

    setFormData((prev) => ({
      ...prev,
      dateBegin: prev.dateBegin || minDate,
      dateEnd: prev.dateEnd || minDate,
    }));
  }, []);

  const isFormValid = useMemo(() => {
    const required = [
      formData.description,
      formData.client,
      formData.place,
      formData.dateBegin,
      formData.dateEnd,
    ];

    const allFilled = required.every((v) => String(v).trim() !== "");

    if (formData.dateBegin && formData.dateEnd) {
      const begin = new Date(formData.dateBegin);
      const end = new Date(formData.dateEnd);
      return allFilled && end >= begin;
    }

    return allFilled;
  }, [formData]);

  useEffect(() => {
    if (!formData.dateBegin || !formData.dateEnd) {
      setDateError("");
      return;
    }

    const begin = new Date(formData.dateBegin);
    const end = new Date(formData.dateEnd);

    if (end < begin) {
      setDateError("Дата окончания не может быть раньше даты начала");
    } else {
      setDateError("");
    }
  }, [formData.dateBegin, formData.dateEnd]);

  // redirect after success
  useEffect(() => {
    if (!successOpen) return;
    const t = setTimeout(() => navigate("/dashboard"), 3000);
    return () => clearTimeout(t);
  }, [successOpen, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // align dateEnd with dateBegin if needed
    if (
      name === "dateBegin" &&
      formData.dateEnd &&
      new Date(value) > new Date(formData.dateEnd)
    ) {
      setFormData((prev) => ({ ...prev, dateBegin: value, dateEnd: value }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggleChange = (value) => {
    setFormData((prev) => ({ ...prev, toggle: value }));
  };

  async function safeReadResponse(response) {
    const ct = response.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      try {
        return await response.json();
      } catch {
        return {};
      }
    }
    try {
      const text = await response.text();
      return { details: text };
    } catch {
      return {};
    }
  }

  function normalizeErrorMessage(status, data) {
    const raw =
      data?.details ||
      data?.error ||
      data?.message ||
      (typeof data === "string" ? data : "") ||
      "";

    if (status === 403) return raw || "Недостаточно прав";
    if (status === 401) return raw || "Требуется авторизация";
    if (status >= 500) return raw || "Ошибка сервера";
    return raw || "Ошибка при отправке";
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    setSuccessOpen(false);

    if (!isFormValid || submitting) {
      setErrorTitle("Проверьте поля");
      setErrorMessage("Заполните обязательные поля корректно.");
      return;
    }

    setSubmitting(true);

    try {
      // 1С ждёт: type (0 - обычная сделка, 1 - сухая аренда)
      const type = formData.toggle === "rent" ? 1 : 0;

      const resp = await fetch("/api/lead/create-lead", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          type,
          description: formData.description,
          client: formData.client,
          place: formData.place,
          eventName: formData.eventName,
          dateBegin: formData.dateBegin,
          dateEnd: formData.dateEnd,
        }),
      });

      const data = await safeReadResponse(resp);

      if (resp.ok) {
        const msg = data?.details || data?.message || "Заявка успешно создана";
        setSuccessMessage(typeof msg === "string" ? msg : JSON.stringify(msg));
        setSuccessOpen(true);

        setFormData((prev) => ({
          ...prev,
          description: "",
          client: "",
          place: "",
          eventName: "",
        }));
      } else {
        setErrorTitle("Не удалось отправить");
        setErrorMessage(normalizeErrorMessage(resp.status, data));
      }
    } catch (err) {
      setErrorTitle("Ошибка сети");
      setErrorMessage(
        "Не удалось связаться с сервером. " + (err?.message || "")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentUser) return <div>Загрузка...</div>;

  return (
    <div className={styles.container}>
      {successOpen && (
        <div className={styles.successOverlay} role="dialog" aria-modal="true">
          <div className={styles.successModal}>
            <div className={styles.successIcon} aria-hidden="true" />
            <h3>Успешно!</h3>
            <p>{successMessage}</p>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} />
            </div>
            <p className={styles.redirectText}>
              Переход в кабинет через 3 секунды...
            </p>
          </div>
        </div>
      )}

      <div className={styles.formContainer}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.topControls}>
            <div className={styles.toggle}>
              <input
                type="radio"
                name="toggle"
                id="deal"
                value="deal"
                checked={formData.toggle === "deal"}
                onChange={() => handleToggleChange("deal")}
                className={styles.toggleInput}
              />
              <label
                className={`${styles.toggleLabel} ${
                  formData.toggle === "deal" ? styles.active : ""
                }`}
                htmlFor="deal"
              >
                Обычная сделка
              </label>

              <input
                type="radio"
                name="toggle"
                id="rent"
                value="rent"
                checked={formData.toggle === "rent"}
                onChange={() => handleToggleChange("rent")}
                className={styles.toggleInput}
              />
              <label
                className={`${styles.toggleLabel} ${
                  formData.toggle === "rent" ? styles.active : ""
                }`}
                htmlFor="rent"
              >
                Сухая аренда
              </label>
            </div>

            <button
              onClick={() => navigate("/dashboard")}
              className={styles.backButton}
              type="button"
            >
              Назад
            </button>
          </div>

          <div className={styles.fieldsGrid}>
            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="description">
                Описание заявки: *
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                className={styles.input}
              />
            </div>

            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="client">
                Клиент: *
              </label>
              <input
                type="text"
                id="client"
                name="client"
                value={formData.client}
                onChange={handleChange}
                required
                className={styles.input}
              />
            </div>

            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="place">
                Место проведения: *
              </label>
              <input
                type="text"
                id="place"
                name="place"
                value={formData.place}
                onChange={handleChange}
                required
                className={styles.input}
              />
            </div>

            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="eventName">
                Название программы (опционально):
              </label>
              <input
                type="text"
                id="eventName"
                name="eventName"
                value={formData.eventName}
                onChange={handleChange}
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.datesGrid}>
            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="dateBegin">
                Дата начала: *
              </label>
              <input
                type="date"
                id="dateBegin"
                name="dateBegin"
                value={formData.dateBegin}
                onChange={handleChange}
                required
                className={styles.input}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>

            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="dateEnd">
                Дата окончания: *
              </label>
              <input
                type="date"
                id="dateEnd"
                name="dateEnd"
                value={formData.dateEnd}
                onChange={handleChange}
                required
                className={`${styles.input} ${
                  dateError ? styles.inputError : ""
                }`}
                min={formData.dateBegin || new Date().toISOString().split("T")[0]}
              />
              {dateError && <div className={styles.dateError}>{dateError}</div>}
            </div>
          </div>

          {(errorTitle || errorMessage) && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              <div>
                <p className={styles.alertText}>{errorTitle || "Ошибка"}</p>
                {errorMessage && (
                  <p className={styles.alertSubText}>{errorMessage}</p>
                )}
              </div>
              <button
                type="button"
                className={styles.alertButton}
                onClick={clearError}
              >
                Понятно
              </button>
            </div>
          )}

          <button
            type="submit"
            className={styles.sendButton}
            disabled={!isFormValid || submitting}
          >
            {submitting ? "Создание..." : "Создать программу"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default LeadForm;